import React, { useState } from 'react';
import { enqueueSnackbar } from 'notistack';
import { Button } from '../../../components/ui/button';
import { DatePickerField } from '../../../components/DatePickerField';
import { fechamentosService } from '../../../services/fechamentos';

interface CreateFechamentoModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  grupoId: string;
}

export const CreateFechamentoModal: React.FC<CreateFechamentoModalProps> = ({
  isOpen,
  onClose,
  onSave,
  grupoId
}) => {
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');
  const [observacoes, setObservacoes] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!dataInicio || !dataFim) {
      enqueueSnackbar('Preencha as datas de início e fim', { variant: 'error' });
      return;
    }

    try {
      setLoading(true);
      // Criar datas no timezone local para evitar problemas de conversão UTC
      const dataInicioLocal = new Date(dataInicio + 'T00:00:00');
      const dataFimLocal = new Date(dataFim + 'T23:59:59');

      await fechamentosService.criar({
        id_grupo: grupoId,
        data_inicio: dataInicioLocal,
        data_fim: dataFimLocal,
        observacoes
      });
      enqueueSnackbar('Fechamento criado com sucesso!', { variant: 'success' });
      onSave();
      setDataInicio('');
      setDataFim('');
      setObservacoes('');
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || 'Erro ao criar fechamento', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-[9999] flex items-center justify-center">
      <div className="bg-card rounded-lg shadow-lg p-6 w-full max-w-md mx-4">
        <h2 className="text-xl font-semibold text-foreground mb-4">Novo Fechamento</h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-1">
              Data de Início
            </label>
            <DatePickerField
              value={dataInicio}
              onChange={(value) => setDataInicio(value)}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-1">
              Data de Fim
            </label>
            <DatePickerField
              value={dataFim}
              onChange={(value) => setDataFim(value)}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-1">
              Observações (opcional)
            </label>
            <textarea
              value={observacoes}
              onChange={(e) => setObservacoes(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              placeholder="Observações sobre o fechamento..."
            />
          </div>

          <div className="flex justify-end space-x-3">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Criando...
                </>
              ) : (
                'Criar Fechamento'
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
